import React from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Link } from 'core/routing';

import { Avatar } from 'apps/components/shared';
import { media, hexa } from 'legacyComponents/styled/styleUtils';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';

import { Commenters } from 'apps/components/commenters';

import {
  PostCard,
  TextWrapper,
  FullWidthTitle as Title,
  PostCardActions,
} from 'legacyComponents/cards/styles';
import { Date } from 'apps/components/date';

import { clientStore } from 'apps/client/app';
import { ContentLink } from 'apps/utils';
import { imageSrc } from 'apps/file/utils';
import { conf as mainConf } from 'apps/config.yaml';
import { Badge, Button, Tooltip, Paragraph } from 'components';

export const IssueCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns }) => {
    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='issue' />;
    }

    const {
      title,
      slug,
      creatorProfile,
      commentsCount,
      dateCreated,
      labels,
      commenters,
      inSpace,
      content,
    } = cardObject;
    const projectSlug = `${inSpace.parent.path} / ${inSpace.content.slug}`;
    const spacePath = projectSlug.includes('@')
      ? 'profile.project'
      : 'initiative.project';
    const spaceParams =
      spacePath === 'profile.project'
        ? { profile: inSpace.parent.path, slug: inSpace.content.slug }
        : { initiative: inSpace.parent.path, slug: inSpace.content.slug };

    const blockLabels = [];
    labels.forEach(label => {
      blockLabels.push(
        <BadgeStyled
          key={`badge-${label.id}`}
          text={label.name}
          size='xsmall'
          customBgColor={`#${label.color}`}
        />,
      );
    });
    return (
      <PostCard
        data-intercom-target={cardNo === 0 ? 'project-issues-list' : ''}
      >
        <Tooltip delayShow={500} />
        <Link
          name='profile'
          params={{ profile: creatorProfile.username }}
          content={creatorProfile.content}
        >
          <Avatar
            src={imageSrc(
              creatorProfile.avatar,
              creatorProfile.imageFallbackPath,
              mainConf.imgSizes.profile.small,
            )}
          />
        </Link>
        <TextWrapper>
          <ContentLink
            contentSubPath='issue'
            contentObj={cardObject}
            params={{ slug: inSpace.content.slug, issueSlug: slug }}
            content={content}
          >
            <Title
              truncate={
                title.length > 100 &&
                ['default', 'l'].includes(clientStore.activeBreakPoint)
              }
            >
              {title}
            </Title>
          </ContentLink>
          <Paragraph size='small'>
            Opened in&nbsp;
            <Link name={spacePath} params={spaceParams} content={content}>
              <Trans>
                <Discuss>{projectSlug}</Discuss>
              </Trans>
            </Link>
            <DateStyled value={dateCreated} />
            <ContentLink
              contentSubPath='issue'
              contentObj={cardObject}
              params={{ slug: inSpace.content.slug, issueSlug: slug }}
              content={content}
            >
              <Discuss>
                {commentsCount > 0 ? (
                  <Trans count={commentsCount}>
                    {{ commentsCount }} comment
                  </Trans>
                ) : (
                  <Trans>Discuss</Trans>
                )}
              </Discuss>
            </ContentLink>
          </Paragraph>
          {blockLabels}
        </TextWrapper>
        {commenters && !!commenters.length && (
          <CommentersStyled commenters={commenters} />
        )}
        <PostCardActions>
          <ContentLink
            contentSubPath='issue'
            contentObj={cardObject}
            params={{ slug: inSpace.content.slug, issueSlug: slug }}
            content={content}
          >
            <ButtonStyled
              variant='secondary'
              icon='chat_multiple'
              data-tip='See comments'
              outline
            />
          </ContentLink>
        </PostCardActions>
      </PostCard>
    );
  }),
);

const CommentersStyled = styled(Commenters)`
  margin-left: auto;
  margin-right: ${props => props.theme.spacing.xsmall};
`;

const ButtonStyled = styled(Button)`
  ${media.s`
    display: none;
  `};
`;

const Discuss = styled.span`
  color: ${props => props.theme.color.wfGrey700};
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${props => props.theme.color.wfGrey};
  }
`;

const BadgeStyled = styled(Badge)`
  margin-right: ${props => props.theme.spacing.small};
  margin-top: ${props => props.theme.spacing.small};
  border: 1px solid ${props => props.customBgColor};
  background-color: ${props => hexa(props.customBgColor, 0.1)};
  color: ${props => props.customBgColor};
`;

const DateStyled = styled(Date)`
  &::before,
  &::after {
    content: '|';
    display: inline-block;
    margin: 0 ${props => props.theme.spacing.small};
    color: ${props => props.theme.color.wfGrey700};
  }
`;

IssueCard.propTypes = {
  cardObject: PropTypes.object,
  cardNo: PropTypes.number,
  status: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  author: PropTypes.string,
  datePublished: PropTypes.string,
  labels: PropTypes.array,
  comments: PropTypes.array,
};
